import React, { useState, useEffect, useRef, useCallback } from 'react'
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group'
import { Label } from '@/components/ui/label'
import { Input } from '@/components/ui/input'
import { Badge } from '@/components/ui/badge'
import { Separator } from '@/components/ui/separator'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { supabase } from '../supabase-client'
import { toast } from 'sonner'
import {
  CheckCircle,
  AlertCircle,
  Search,
  Sparkles,
  ExternalLink,
  Info,
  Loader2
} from 'lucide-react'

/**
 * Enhanced GoCardlessMatchingModal Component
 *
 * Features:
 * - Fuzzy match suggestions with confidence scores
 * - Debounced search functionality
 * - Mandate selection for customers with multiple mandates
 * - Status badges for mandate states
 * - Source of truth: gocardless_customer_mappings table
 *
 * @param {boolean} open - Modal visibility state
 * @param {Function} onClose - Close handler
 * @param {Object} customer - Odin customer object { id, name, email }
 * @param {Function} onSuccess - Success callback
 */
export function GoCardlessMatchingModal({ open, onClose, customer, onSuccess }) {
  // State management
  const [fuzzyMatches, setFuzzyMatches] = useState([])
  const [searchResults, setSearchResults] = useState([])
  const [searchQuery, setSearchQuery] = useState('')
  const [selectedGcCustomerId, setSelectedGcCustomerId] = useState(null)
  const [selectedMandateId, setSelectedMandateId] = useState(null)
  const [loading, setLoading] = useState(true)
  const [searching, setSearching] = useState(false)
  const [linking, setLinking] = useState(false)
  const [showMandateSelection, setShowMandateSelection] = useState(false)

  // Refs for debouncing
  const searchTimerRef = useRef(null)

  // Initialize: fetch fuzzy matches when modal opens
  useEffect(() => {
    if (open && customer) {
      fetchFuzzyMatches()
      setSearchQuery('') // Reset search
      setSearchResults([])
      setSelectedGcCustomerId(null)
      setSelectedMandateId(null)
      setShowMandateSelection(false)
    }

    return () => {
      if (searchTimerRef.current) {
        clearTimeout(searchTimerRef.current)
      }
    }
  }, [open, customer])

  /**
   * Fetch all available (unmapped) mandates from GoCardless
   */
  const fetchFuzzyMatches = async () => {
    setLoading(true)

    try {
      const { data, error } = await supabase.functions.invoke('gocardless-customers', {
        body: {
          action: 'list-available-mandates',
          odinCustomerId: customer?.id
        }
      })

      if (error) throw error

      const availableMandates = data?.mandates || []

      // Surface any warnings from the handler (e.g. API failures for specific GC customers)
      if (data?.warnings?.length > 0) {
        console.warn('[GoCardlessMatchingModal] Warnings from mandate fetch:', data.warnings)
        toast.warning('Some mandates could not be loaded', {
          description: `${data.warnings.length} GoCardless customer(s) had API errors`
        })
      }

      // Transform flat mandate list into customer-grouped structure
      // that renderCustomerCard expects
      const customerMap = new Map()

      for (const mandateRecord of availableMandates) {
        const customerId = mandateRecord.gcCustomerId

        if (!customerMap.has(customerId)) {
          // Create new customer entry
          customerMap.set(customerId, {
            id: customerId,
            gcCustomerId: customerId,
            name: mandateRecord.gcCustomerName,
            email: mandateRecord.gcCustomerEmail,
            given_name: mandateRecord.gcCustomerGivenName,
            family_name: mandateRecord.gcCustomerFamilyName,
            company_name: mandateRecord.gcCustomerCompanyName,
            mandates: []
          })
        }

        // Add mandate to customer's mandates array
        const customer = customerMap.get(customerId)
        customer.mandates.push({
          id: mandateRecord.mandateId,
          status: mandateRecord.mandateStatus,
          scheme: mandateRecord.mandateScheme,
          reference: mandateRecord.mandateReference,
          created_at: mandateRecord.mandateCreatedAt
        })
      }

      // Convert Map to array
      const transformedCustomers = Array.from(customerMap.values())
      setFuzzyMatches(transformedCustomers)
    } catch (error) {
      console.error('[GoCardlessMatchingModal] Error fetching available mandates:', error)
      toast.error('Failed to load available mandates', {
        description: error.message
      })
    } finally {
      setLoading(false)
    }
  }

  /**
   * Debounced search handler
   */
  const handleSearchChange = (e) => {
    const query = e.target.value
    setSearchQuery(query)

    // Clear previous timer
    if (searchTimerRef.current) {
      clearTimeout(searchTimerRef.current)
    }

    // Don't search for queries less than 2 characters
    if (query.trim().length < 2) {
      setSearchResults([])
      setSearching(false)
      return
    }

    // Set debounce timer (400ms like Xero)
    setSearching(true)
    searchTimerRef.current = setTimeout(() => {
      performSearch(query)
    }, 400)
  }

  /**
   * Perform search against GoCardless API
   */
  const performSearch = async (query) => {
    try {
      const { data, error } = await supabase.functions.invoke('gocardless-customers', {
        body: {
          action: 'search-customers',
          query: query.trim()
        }
      })

      if (error) throw error

      setSearchResults(data?.customers || [])
    } catch (error) {
      console.error('[GoCardlessMatchingModal] Search error:', error)
      toast.error('Search failed', {
        description: error.message
      })
      setSearchResults([])
    } finally {
      setSearching(false)
    }
  }

  /**
   * Handle customer selection
   */
  const handleCustomerSelect = useCallback((gcCustomerId, mandates) => {
    setSelectedGcCustomerId(gcCustomerId)

    if (mandates && mandates.length > 1) {
      // Multiple mandates - show selection UI
      setShowMandateSelection(true)
      setSelectedMandateId(null) // Force user to select
    } else if (mandates && mandates.length === 1) {
      // Single mandate - auto-select
      setSelectedMandateId(mandates[0].id)
      setShowMandateSelection(false)
    } else {
      // No mandates
      setSelectedMandateId(null)
      setShowMandateSelection(false)
    }
  }, [])

  /**
   * Handle mandate selection (for customers with multiple mandates)
   */
  const handleMandateSelect = (mandateId) => {
    setSelectedMandateId(mandateId)
  }

  /**
   * Link customer to selected GoCardless customer/mandate
   */
  const handleLink = async () => {
    if (!selectedGcCustomerId) {
      toast.error('Please select a GoCardless customer')
      return
    }

    if (!selectedMandateId) {
      toast.error('Please select a mandate')
      return
    }

    setLinking(true)

    try {
      const { data: { session } } = await supabase.auth.getSession()
      const userId = session?.user?.id

      const { data, error } = await supabase.functions.invoke('gocardless-customers', {
        body: {
          action: 'link-customer',
          odinCustomerId: customer.id,
          gcCustomerId: selectedGcCustomerId,
          mandateId: selectedMandateId,
          mappingType: 'manual',
          userId
        }
      })

      if (error) throw error

      toast.success('Customer linked successfully', {
        description: 'Direct Debit payments are now enabled for this customer'
      })

      onSuccess(data?.mapping)
      onClose()
    } catch (error) {
      console.error('[GoCardlessMatchingModal] Link error:', error)
      toast.error('Failed to link customer', {
        description: error.message
      })
    } finally {
      setLinking(false)
    }
  }

  /**
   * Get mandate status badge variant
   */
  const getMandateStatusBadgeVariant = (status) => {
    switch (status) {
      case 'active':
        return 'success'
      case 'pending_customer_approval':
      case 'pending_submission':
        return 'warning'
      case 'failed':
      case 'cancelled':
      case 'expired':
        return 'destructive'
      default:
        return 'secondary'
    }
  }

  /**
   * Format mandate status for display
   */
  const formatMandateStatus = (status) => {
    return status
      .split('_')
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ')
  }

  /**
   * Render customer card (for fuzzy matches and search results)
   */
  const renderCustomerCard = (
    gcCustomer,
    confidenceScore = null,
    matchReason = null,
    isFuzzyMatch = false
  ) => {
    const isSelected = selectedGcCustomerId === gcCustomer.id
    const isAlreadyMapped = gcCustomer.isAlreadyMapped || false

    return (
      <div
        key={gcCustomer.id}
        className={`border rounded-lg p-3 transition-all ${
          isSelected ? 'ring-2 ring-primary bg-primary/5' : 'hover:bg-muted/50'
        } ${isAlreadyMapped ? 'opacity-50' : 'cursor-pointer'}`}
        onClick={() => !isAlreadyMapped && handleCustomerSelect(gcCustomer.id, gcCustomer.mandates)}
      >
        <div className="flex items-start gap-3">
          <RadioGroupItem
            value={gcCustomer.id}
            id={gcCustomer.id}
            disabled={isAlreadyMapped}
            className="mt-1"
          />
          <div className="flex-1 min-w-0">
            <div className="flex items-start justify-between gap-2">
              <div className="flex-1 min-w-0">
                <div className="font-semibold truncate">{gcCustomer.name}</div>
                {gcCustomer.email && (
                  <div className="text-sm text-muted-foreground truncate">{gcCustomer.email}</div>
                )}
              </div>
              {isFuzzyMatch && confidenceScore !== null && (
                <Badge variant="secondary" className="shrink-0">
                  {Math.round(confidenceScore * 100)}% match
                </Badge>
              )}
            </div>

            {matchReason && (
              <div className="text-xs text-muted-foreground mt-1">{matchReason}</div>
            )}

            {isAlreadyMapped && (
              <Badge variant="destructive" className="mt-2">
                Already Mapped
              </Badge>
            )}

            {!isAlreadyMapped && gcCustomer.mandates && gcCustomer.mandates.length > 0 && (
              <div className="mt-2 space-y-1">
                <div className="text-xs font-medium text-muted-foreground">
                  {gcCustomer.mandates.length === 1
                    ? 'Mandate'
                    : `${gcCustomer.mandates.length} Mandates`}
                </div>
                {gcCustomer.mandates.slice(0, 2).map((mandate) => (
                  <div key={mandate.id} className="flex items-center gap-2 text-xs">
                    <Badge variant={getMandateStatusBadgeVariant(mandate.status)} className="text-xs">
                      {formatMandateStatus(mandate.status)}
                    </Badge>
                    <span className="text-muted-foreground">{mandate.reference || mandate.id}</span>
                  </div>
                ))}
                {gcCustomer.mandates.length > 2 && (
                  <div className="text-xs text-muted-foreground">
                    +{gcCustomer.mandates.length - 2} more
                  </div>
                )}
              </div>
            )}

            {!isAlreadyMapped && (!gcCustomer.mandates || gcCustomer.mandates.length === 0) && (
              <div className="mt-2 flex items-center gap-1 text-xs text-yellow-600">
                <AlertCircle className="h-3 w-3" />
                No mandates available
              </div>
            )}
          </div>
        </div>
      </div>
    )
  }

  /**
   * Render mandate selection UI (for customers with multiple mandates)
   */
  const renderMandateSelection = () => {
    if (!showMandateSelection || !selectedGcCustomerId) return null

    const selectedCustomer =
      [...fuzzyMatches, ...searchResults].find((c) => c.gcCustomerId === selectedGcCustomerId) ||
      searchResults.find((c) => c.id === selectedGcCustomerId)

    const mandates = selectedCustomer?.mandates || []

    return (
      <div className="space-y-3">
        <Separator />
        <div>
          <Label className="text-sm font-medium">Select Mandate</Label>
          <p className="text-xs text-muted-foreground mt-1">
            This customer has multiple mandates. Please select one.
          </p>
        </div>

        <RadioGroup value={selectedMandateId} onValueChange={handleMandateSelect}>
          <div className="space-y-2">
            {mandates.map((mandate) => (
              <div
                key={mandate.id}
                className={`flex items-start space-x-3 border rounded p-3 cursor-pointer transition-colors ${
                  selectedMandateId === mandate.id
                    ? 'ring-2 ring-primary bg-primary/5'
                    : 'hover:bg-muted/50'
                }`}
                onClick={() => handleMandateSelect(mandate.id)}
              >
                <RadioGroupItem value={mandate.id} id={mandate.id} className="mt-1" />
                <Label htmlFor={mandate.id} className="flex-1 cursor-pointer">
                  <div className="flex items-center gap-2">
                    <Badge variant={getMandateStatusBadgeVariant(mandate.status)}>
                      {formatMandateStatus(mandate.status)}
                    </Badge>
                    <span className="text-sm font-medium">
                      {mandate.reference || mandate.id}
                    </span>
                  </div>
                  <div className="text-xs text-muted-foreground mt-1">
                    Created: {new Date(mandate.created_at).toLocaleDateString()}
                    {mandate.scheme && ` • ${mandate.scheme.toUpperCase()}`}
                  </div>
                </Label>
              </div>
            ))}
          </div>
        </RadioGroup>
      </div>
    )
  }

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle>Link Customer to GoCardless Mandate</DialogTitle>
        </DialogHeader>

        <div className="space-y-4 flex-1 overflow-hidden flex flex-col">
          {/* Odin Customer Info */}
          <div className="bg-muted/30 p-3 rounded-lg">
            <Label className="text-sm text-muted-foreground">Odin Customer</Label>
            <div className="font-semibold">{customer?.name || 'Unknown'}</div>
            {customer?.email && (
              <div className="text-sm text-muted-foreground">{customer.email}</div>
            )}
          </div>

          {/* Info Alert */}
          <Alert>
            <Info className="h-4 w-4" />
            <AlertDescription className="text-xs">
              Mandates must be set up in the{' '}
              <a
                href="https://manage.gocardless.com"
                target="_blank"
                rel="noopener noreferrer"
                className="font-medium underline inline-flex items-center gap-1"
              >
                GoCardless dashboard
                <ExternalLink className="h-3 w-3" />
              </a>
              . You can only link to existing customers and mandates here.
            </AlertDescription>
          </Alert>

          {/* Available Mandates */}
          {!loading && fuzzyMatches.length > 0 && (
            <div className="flex-1 overflow-auto">
              <div className="flex items-center gap-2 mb-2">
                <CheckCircle className="h-4 w-4 text-primary" />
                <Label className="text-sm font-medium">
                  Available Mandates ({fuzzyMatches.length})
                </Label>
              </div>
              <RadioGroup value={selectedGcCustomerId} onValueChange={setSelectedGcCustomerId}>
                <div className="space-y-2">
                  {fuzzyMatches.map((gcCustomer) =>
                    renderCustomerCard(gcCustomer, null, null, false)
                  )}
                </div>
              </RadioGroup>
            </div>
          )}

          {/* Search Section */}
          <div>
            {fuzzyMatches.length > 0 && <Separator className="mb-4" />}
            <div className="flex items-center gap-2 mb-2">
              <Search className="h-4 w-4" />
              <Label htmlFor="search" className="text-sm font-medium">
                Search GoCardless Customers
              </Label>
            </div>
            <Input
              id="search"
              type="text"
              placeholder="Search by name or email (min 2 characters)..."
              value={searchQuery}
              onChange={handleSearchChange}
              disabled={loading}
            />
            {searching && (
              <div className="flex items-center gap-2 mt-2 text-xs text-muted-foreground">
                <Loader2 className="h-3 w-3 animate-spin" />
                Searching...
              </div>
            )}
          </div>

          {/* Search Results */}
          {searchQuery.length >= 2 && !searching && searchResults.length > 0 && (
            <div className="flex-1 overflow-auto">
              <Label className="text-sm font-medium mb-2 block">Search Results</Label>
              <RadioGroup value={selectedGcCustomerId} onValueChange={setSelectedGcCustomerId}>
                <div className="space-y-2">
                  {searchResults.map((gcCustomer) =>
                    renderCustomerCard(gcCustomer, null, null, false)
                  )}
                </div>
              </RadioGroup>
            </div>
          )}

          {searchQuery.length >= 2 && !searching && searchResults.length === 0 && (
            <div className="py-6 text-center text-muted-foreground">
              <AlertCircle className="h-10 w-10 mx-auto mb-2 text-muted-foreground/50" />
              <p className="font-medium">No customers found</p>
              <p className="text-sm mt-1">Try a different search term</p>
            </div>
          )}

          {/* Mandate Selection (if needed) */}
          {renderMandateSelection()}

          {/* Loading State */}
          {loading && (
            <div className="py-8 text-center">
              <Loader2 className="h-8 w-8 animate-spin mx-auto mb-2 text-primary" />
              <p className="text-sm text-muted-foreground">Loading suggestions...</p>
            </div>
          )}

          {/* No Available Mandates State */}
          {!loading && fuzzyMatches.length === 0 && searchQuery.length < 2 && (
            <div className="py-6 text-center text-muted-foreground">
              <Info className="h-10 w-10 mx-auto mb-2 text-muted-foreground/50" />
              <p className="font-medium">No available mandates</p>
              <p className="text-sm mt-1">
                All GoCardless mandates are already linked, or no mandates exist yet
              </p>
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={linking}>
            Cancel
          </Button>
          <Button
            onClick={handleLink}
            disabled={linking || !selectedGcCustomerId || !selectedMandateId || loading}
          >
            {linking ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin mr-2" />
                Linking...
              </>
            ) : (
              'Link Customer'
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

export default GoCardlessMatchingModal
